import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Play, 
  Trash2, 
  ArrowLeft, 
  Calendar,
  Clock,
  Crosshair
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import './QuizBlasterHub.css';

const QuizBlasterHub = () => {
  const navigate = useNavigate();
  const [savedQuizzes, setSavedQuizzes] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    loadSavedQuizzes();
  }, []);

  const loadSavedQuizzes = () => {
    try {
      const data = localStorage.getItem('quizBlasterDatabase');
      const quizzes = data ? JSON.parse(data) : [];
      console.log('📂 Loaded quiz blaster quizzes:', quizzes);
      setSavedQuizzes(quizzes);
    } catch (error) {
      console.error('Error loading saved quizzes:', error);
      toast.error('Failed to load saved quizzes');
    }
  };

  const createNewQuiz = () => {
    navigate('/quiz-blaster-game');
  };

  const playQuiz = (quizId) => {
    navigate(`/quiz-blaster-game?quizId=${quizId}`);
  };

  const deleteQuiz = (quizId) => {
    try {
      const data = localStorage.getItem('quizBlasterDatabase');
      const quizzes = data ? JSON.parse(data) : [];
      const filteredQuizzes = quizzes.filter(q => q.id !== quizId);
      localStorage.setItem('quizBlasterDatabase', JSON.stringify(filteredQuizzes));
      
      loadSavedQuizzes();
      setShowDeleteConfirm(null);
      toast.success('Quiz deleted successfully');
    } catch (error) {
      console.error('Error deleting quiz:', error);
      toast.error('Failed to delete quiz');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="quiz-blaster-hub-page">
      <div className="quiz-blaster-hub-container">
        <div className="hub-header">
          <Button 
            onClick={() => navigate('/dashboard')} 
            className="back-button"
            variant="ghost"
          >
            <ArrowLeft size={20} />
            Back
          </Button>
          <div className="hub-title">
            <h1>🎯 Quiz Blaster Hub</h1>
            <p>Your saved blaster quizzes</p>
          </div>
        </div>

        <motion.div
          className="create-new-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button onClick={createNewQuiz} className="create-new-btn">
            <Plus size={24} />
            Create New Quiz
          </Button>
        </motion.div>

        <div className="quizzes-grid">
          {savedQuizzes.map((quiz, index) => (
              <motion.div
                key={quiz.id}
                className="quiz-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="quiz-card-header">
                  <div className="quiz-icon">🎯</div>
                  <div className="quiz-info">
                    <h3 className="quiz-title">{quiz.title || 'Untitled Quiz'}</h3>
                    <div className="quiz-meta">
                      <span className="meta-item">
                        <Calendar size={14} />
                        {formatDate(quiz.createdAt)}
                      </span>
                      <span className="meta-item">
                        <Clock size={14} />
                        {formatTime(quiz.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="quiz-details">
                  <p className="quiz-question">❓ {quiz.questions?.length || 0} questions</p>
                </div>

                <div className="quiz-card-actions">
                  <Button 
                    onClick={() => playQuiz(quiz.id)} 
                    className="play-btn"
                  >
                    <Play size={18} />
                    Play
                  </Button>
                  <Button 
                    onClick={() => setShowDeleteConfirm(quiz.id)} 
                    className="delete-btn"
                    variant="ghost"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            className="delete-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              className="delete-dialog"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Delete Quiz?</h3>
              <p>This action cannot be undone.</p>
              <div className="delete-actions">
                <Button 
                  onClick={() => setShowDeleteConfirm(null)} 
                  variant="ghost"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => deleteQuiz(showDeleteConfirm)} 
                  className="confirm-delete-btn"
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuizBlasterHub;
